import { useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { controlClass, controlShellClass } from "../lib/control.js";
import { useAnchoredPanel } from "../lib/useAnchoredPanel.js";
import { portalHost } from "../lib/portalHost.js";
import { cn } from "../lib/utils.js";
import { resolveOptionsLoadState, type OptionsLoadState } from '../lib/optionsLoadState.js';

export interface ComboboxOption {
  value: string;
  label: string;
}

export interface ComboboxProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  options: ComboboxOption[];
  placeholder?: string;
  disabled?: boolean;
  /**
   * Mark the field mandatory. This is the one control in the family with NO
   * native fallback — it is a `role="combobox"` text input that never submits,
   * so `required` would do nothing and `aria-required` is the only way the
   * state reaches assistive tech at all. Pair with `<Label required>`.
   */
  required?: boolean;
  className?: string;
  /** Copy shown when the query matches no option. */
  emptyText?: string;
  /**
   * WHAT HAS ALREADY BEEN CHOSEN, DRAWN INSIDE THE FIELD.
   *
   * Pass the chips and the box becomes a container: they sit on the same line
   * the reader types on, the border wraps both, and the field grows down as
   * they accumulate. Omit it and this is the plain single-line combobox every
   * other caller uses — nothing about their rendering changes.
   *
   * WHY IT BELONGS TO THE PRIMITIVE. A caller can draw chips under the box in
   * two lines of its own markup, and that is what the announcement composer
   * did: a bordered input, then a separate row. It reads as two controls
   * because it IS two, and the alternative — a page styling a `<div>` to look
   * like a field around a borderless input — is a second control language for
   * the one thing the system already owns (§6). So the field learns to hold
   * tokens once, here, where the focus ring, the disabled skin and the
   * coarse-pointer floor are already correct.
   *
   * The caller still owns what a chip IS and what removing one means; this only
   * says where they are drawn.
   */
  tokens?: ReactNode;
  /**
   * WHAT THE READER HAS TYPED, reported out as it changes — for a caller whose
   * list cannot be held in memory.
   *
   * The control still filters `options` itself; this does not replace that. It
   * exists so a caller can go and FETCH the options for a query it could not
   * have supplied up front (an employee type-ahead over a roster of hundreds is
   * the case it was added for), then hand them back through `options`. Omit it
   * and nothing changes — every existing caller passes a complete list and this
   * is never called.
   *
   * Called with `""` whenever the box empties or closes, so a caller can stop
   * asking rather than leaving a stale query in flight.
   */
  onQueryChange?: (query: string) => void;
  /**
   * Whether the OPTIONS are loading or failed to load — see
   * `lib/optionsLoadState.ts`. A failed list blocks the control and says so in
   * place of the placeholder, so a picker whose fetch died never reads as a
   * picker with nothing to offer. Omit it and nothing changes.
   */
  loadState?: OptionsLoadState;
  "aria-label"?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
}

/**
 * Searchable single-select — the type-to-filter control the app was missing.
 * Native `<select>` is fine for short lists, but painful for long ones (the
 * IANA timezone list being the worst offender). This filters options by label
 * *or* value as you type, is fully keyboard-operable (↑/↓/Enter/Esc), and
 * commits only real option values — typing then blurring reverts to the current
 * selection rather than saving free text.
 *
 * Styling matches the `Input` primitive so it drops into any form unchanged.
 */
export function Combobox({
  id,
  value,
  onChange,
  options,
  placeholder,
  disabled,
  required,
  className,
  emptyText = "No matches",
  tokens,
  onQueryChange,
  loadState,
  "aria-label": ariaLabel,
  "aria-describedby": ariaDescribedby,
  "aria-invalid": ariaInvalid,
}: ComboboxProps): JSX.Element {
  const load = resolveOptionsLoadState(loadState, placeholder);
  const [open, setOpen] = useState(false);
  const [query, setQueryState] = useState("");
  /* One writer for the query, so the report to `onQueryChange` cannot be
     forgotten at one of the five places the query is reset. */
  const setQuery = (next: string): void => {
    setQueryState(next);
    onQueryChange?.(next);
  };
  const [activeIndex, setActiveIndex] = useState(0);
  const listId = useId();

  /* ── THE LIST IS PORTALLED, AND THAT IS THE WHOLE POINT ──────────────────
   *
   * It used to be `absolute z-50` inside this component's own `relative`
   * wrapper, which is correct right up until an ancestor clips — and the most
   * important caller does exactly that. `FiltersPopover` puts a `maxHeight`
   * and `overflowY: 'auto'` on its panel, so a list opening inside it was cut
   * off by a box that knows nothing about it, and on a short panel — one
   * Department field, which is the common case — there was no visible list at
   * all. Every department, designation, nationality, supplier and legal-entity
   * filter in the app is that pairing, so "the list in the filters popup does
   * not show" was all of them at once.
   *
   * `z-50` could never have fixed it. A stacking order decides what is drawn
   * ON TOP; it has no opinion about what is CLIPPED, and the clip is applied
   * by the scroll container before z-index is consulted.
   *
   * This is the same bug `FiltersPopover`'s own header describes and escapes —
   * "a child that escapes, or fails to escape, a stacking or clipping context
   * its parent never declared for it" — reproduced one level down by the fix
   * for it. So it takes the same answer, through the same hook `RowActions`
   * and the popover already measure with, rather than a third copy of the
   * arithmetic.
   */
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  // `stretch`: a listbox is its input's dropdown, so it takes the input's width
  // and leading edge rather than the trailing alignment a row menu wants.
  const pos = useAnchoredPanel(open, inputRef, listRef, { stretch: true });

  const selectedLabel = useMemo(
    () => options.find((o) => o.value === value)?.label ?? value,
    [options, value],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q),
    );
  }, [options, query]);

  function closeAndRevert(): void {
    setOpen(false);
    setQuery("");
  }

  function openList(): void {
    if (disabled === true || load.blocked) return;
    setQuery("");
    setActiveIndex(
      Math.max(
        0,
        filtered.findIndex((o) => o.value === value),
      ),
    );
    setOpen(true);
  }

  /**
   * WHAT THE BOX SAYS WHILE OPEN AND EMPTY.
   *
   * Opening always clears `query` — see `openList` — so a reader who starts
   * typing to search is never editing into the middle of a stale selection.
   * The cost was the box going BLANK the instant it opened: "All departments",
   * a site name, a nationality — visible a moment ago as the field's real
   * value — disappeared with nothing left to say what was chosen, on every
   * field built from this primitive (department, designation, nationality,
   * supplier, legal entity, the timezone picker).
   *
   * The fix is not to change what typing does — an earlier attempt pre-filled
   * `query` with the selection and tried to auto-select it so a keystroke
   * would replace it, and that broke exactly the "click then type" flow this
   * control exists for, because a programmatic selection does not survive
   * synthesized keystrokes the same way a real user's does. The honest fix is
   * a PLACEHOLDER: ghost text in the empty box that names the current
   * selection, in the same muted ink every other placeholder uses, gone the
   * instant a real character is typed because that is what a placeholder is
   * for. The caller's own `placeholder` (e.g. "Search employees…") still wins
   * when there is no selection to show instead.
   */
  const openPlaceholder = selectedLabel || load.placeholder;

  function commit(option: ComboboxOption | undefined): void {
    if (option) onChange(option.value);
    setOpen(false);
    setQuery("");
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>): void {
    if (disabled === true || load.blocked) return;
    if (!open && (e.key === "ArrowDown" || e.key === "Enter")) {
      e.preventDefault();
      openList();
      return;
    }
    if (!open) return;
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
        break;
      case "Enter":
        e.preventDefault();
        commit(filtered[activeIndex]);
        break;
      case "Escape":
        e.preventDefault();
        setOpen(false);
        setQuery("");
        break;
      default:
        break;
    }
  }

  /* TOKEN MODE — the same input, wrapped. Everything about behaviour, ARIA and
     the option list is untouched; what changes is that the border belongs to a
     container the chips share with the input, so the field reads as one thing.
     Clicking anywhere in it lands the caret in the input, which is what makes a
     token field feel like a field rather than a row of buttons. */
  const holdsTokens = tokens !== undefined && tokens !== null && tokens !== false;

  const field = (
      <input
        ref={inputRef}
        id={id}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={
          open && filtered[activeIndex]
            ? `${listId}-opt-${activeIndex}`
            : undefined
        }
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedby}
        aria-invalid={ariaInvalid}
        aria-required={required ? true : undefined}
        autoComplete="off"
        disabled={disabled === true || load.blocked}
        value={open ? query : selectedLabel}
        placeholder={open ? openPlaceholder : load.placeholder}
        onChange={(e) => {
          setQuery(e.target.value);
          setActiveIndex(0);
          if (!open) setOpen(true);
        }}
        onFocus={openList}
        onClick={openList}
        onKeyDown={onKeyDown}
        // Options preventDefault their mousedown so a click keeps focus here and
        // commits before this fires — so blur only means focus truly left, which
        // should close the list and revert any half-typed query.
        onBlur={closeAndRevert}
        /* Shares `controlClass` with Input/Select so the three stay identical
           — unless the field is a token container, in which case the BOX is the
           wrapper and this is just the text cursor inside it: no border of its
           own, no second focus ring, and `w-auto` so it shrinks to the room the
           chips leave rather than pushing every chip onto its own line. */
        className={
          holdsTokens
            ? cn(
                /* No min-width of its own. An `<input>` already refuses to
                   shrink below its intrinsic size, which is the floor that
                   matters here — and any number picked for it would be a
                   measurement of nothing. When the chips fill the line the
                   input wraps to the next one, which is the right answer:
                   somewhere to type, always, rather than a sliver. */
                'flex-1 border-0 bg-transparent p-0 text-copy-lg',
                'text-foreground placeholder:text-muted-foreground',
                'outline-none focus:outline-none focus-visible:outline-none',
                'disabled:cursor-not-allowed disabled:bg-transparent'
              )
            : cn(controlClass, className)
        }
      />
  );

  return (
    <div className="relative">
      {holdsTokens ? (
        <div
          className={cn(controlShellClass, className)}
          aria-invalid={ariaInvalid}
          /* The wrapper is not a control and must not be in the tab order —
             it just hands the caret on. `onMouseDown` rather than `onClick`
             so the focus lands before a chip's own press can steal it. */
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              e.preventDefault();
              inputRef.current?.focus();
            }
          }}
        >
          {tokens}
          {field}
        </div>
      ) : (
        field
      )}
      {open &&
        createPortal(
          <ul
            ref={listRef}
            id={listId}
            role="listbox"
            /* "I belong to whatever opened me." A dismiss-on-outside-click
               handler (FiltersPopover's) would otherwise see a click on an
               option as a click outside itself, because this list is a sibling
               of its panel rather than a descendant. */
            data-portal-surface=""
            /* Parked off-screen until measured rather than flashed at 0,0 —
               `useLayoutEffect` runs before paint, so this is only ever drawn
               if the input has gone away. Same contract as the two other
               portalled panels. */
            style={{
              top: pos?.top ?? -9999,
              left: pos?.left ?? -9999,
              ...(pos ? { maxHeight: pos.maxHeight, width: pos.width } : {}),
            }}
            /* `fixed`, against the portal host — there is no ancestor left to
               be trapped under. `z-50` sits above `FiltersPopover`'s own `z-40`
               panel, which is the one thing z-index IS for here: the list has
               escaped the clip, and now has to draw over the panel it escaped. */
            className="fixed z-50 overflow-auto rounded-panel border border-border bg-surface py-1 shadow-popover"
          >
            {filtered.length === 0 ? (
              <li className="px-4 py-2 text-copy text-muted-foreground">
                {emptyText}
              </li>
            ) : (
              filtered.map((o, i) => (
                <li
                  key={o.value}
                  id={`${listId}-opt-${i}`}
                  role="option"
                  aria-selected={o.value === value}
                  // preventDefault keeps focus on the input so the blur/commit
                  // order is deterministic; selection fires on click.
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => commit(o)}
                  onMouseEnter={() => setActiveIndex(i)}
                  className={cn(
                    // `py-cell-y`/`px-cell-x` — an option list is a one-column
                    // table, so it takes the table's density.
                    "cursor-pointer px-cell-x py-cell-y text-copy",
                    // 44px targets on touch only — on a mouse the list stays
                    // dense, which matters for the 400-entry timezone list.
                    "[@media(pointer:coarse)]:flex [@media(pointer:coarse)]:min-h-touch [@media(pointer:coarse)]:items-center",
                    i === activeIndex
                      ? "bg-muted text-foreground"
                      : "text-foreground",
                    o.value === value && "font-semibold",
                  )}
                >
                  {o.label}
                </li>
              ))
            )}
          </ul>,
          portalHost(),
        )}
    </div>
  );
}
